import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { v2_currentWorkingVessel } from "../actions/v2/user";
import { MSG, formatUserFacingError } from "../constants/userMessages";

const CurrentVesselContext = createContext(null);

const normalizeVessel = (raw) => {
  if (!raw) return null;
  if (raw.vessel_id != null) {
    return {
      vessel_id: raw.vessel_id,
      vessel_name: raw.vessel_name || raw.label || "",
      vessel_code: raw.vessel_code,
      imo_number: raw.imo_number,
    };
  }
  if (raw.vessel?.vessel_id != null) {
    return normalizeVessel(raw.vessel);
  }
  return null;
};

export function CurrentVesselProvider({ children }) {
  const [vessel, setVessel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchVessel = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await v2_currentWorkingVessel();
      if (res?.status) {
        setVessel(normalizeVessel(res.result));
      } else {
        setError(res?.error?.message || res?.error || MSG.loadVesselInfo);
      }
    } catch (err) {
      setError(formatUserFacingError(err.message, MSG.loadVesselInfo));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVessel();
  }, [fetchVessel]);

  const value = useMemo(
    () => ({ vessel, loading, error, refetch: fetchVessel }),
    [vessel, loading, error, fetchVessel]
  );

  return (
    <CurrentVesselContext.Provider value={value}>
      {children}
    </CurrentVesselContext.Provider>
  );
}

export { CurrentVesselContext };

export function useCurrentWorkingVesselContext() {
  const ctx = useContext(CurrentVesselContext);
  if (!ctx) {
    throw new Error("useCurrentWorkingVessel must be used within CurrentVesselProvider");
  }
  return ctx;
}
